import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../configuration/Configuration';
import { useMicrosoftClarity } from './MicrosoftClarityContext';
import { SESSION_EXPIRED_EVENT } from '../configuration/axiox';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [isAuth, setIsAuth] = useState(false);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const clarityHook = useMicrosoftClarity();

  useEffect(() => {
    if (!user) return;
    clarityHook.identifyUser(user);
  }, [user]);

  const fetchUserType = async (username) => {
    try {
      const userTypeResponse = await axios.get(
        `${API_BASE_URL}/api/is-admin/${username}`,
        { withCredentials: true }
      );

      const { is_admin, is_staff, is_regular, is_viewer } =
        userTypeResponse.data;

      // Check if user has checks role from localStorage (for backward compatibility)
      const storedUserType = localStorage.getItem('usertype');
      const isChecks = storedUserType === 'checks';

      // Get department status
      const departmentStatusResponse = await axios.get(
        `${API_BASE_URL}/api/user-department-status/${username}`,
        { withCredentials: true }
      );

      const departmentStatus = departmentStatusResponse.data;

      return {
        isAdmin: is_admin,
        isRegular: is_regular,
        isViewer: is_viewer ?? false,
        isChecks: isChecks,
        isStaff: is_staff,
        departmentStatus: departmentStatus.department_status,
        departmentName: departmentStatus.department_name,
        departmentNames: departmentStatus.department_names,
        deletedAt: departmentStatus.deleted_at,
        hasActiveDepartments: departmentStatus.has_active_departments,
        activeDepartments: departmentStatus.active_departments,
        deletedDepartments: departmentStatus.deleted_departments,
      };
    } catch (error) {
      console.error('Error fetching user type:', error);
      // Fallback to default user type
      return {
        isAdmin: false,
        isRegular: true,
        isViewer: false,
        isChecks: false,
        isStaff: false,
        departmentStatus: 'active',
        hasActiveDepartments: true,
      };
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/users/me/`, {
          withCredentials: true,
        });
        setIsAuth(true);
        setUser(response.data.username);

        const userTypeObj = await fetchUserType(response.data.username);
        setUserType(userTypeObj);
      } catch (error) {
        setIsAuth(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      setIsAuth(false);
      setUser(null);
      setUserType(null);
      localStorage.removeItem('usertype');
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const login = async (credentials) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/users/token/`,
        credentials,
        {
          withCredentials: true,
        }
      );

      const username = response.data.username;

      const userTypeObj = await fetchUserType(username);

      let usertypeStr = null;
      if (userTypeObj.isAdmin) {
        usertypeStr = 'admin';
      } else if (userTypeObj.isStaff) {
        usertypeStr = 'regional';
      } else if (userTypeObj.isRegular) {
        usertypeStr = 'regular';
      } else if (userTypeObj.isChecks) {
        usertypeStr = 'checks';
      }

      if (usertypeStr) {
        localStorage.setItem('usertype', usertypeStr);
      }

      setUser(response.data.username);
      setUserType(userTypeObj);
      setIsAuth(true);
      setIsLoading(false); // Set loading to false on success

      return { success: true, data: response.data };
    } catch (error) {
      // Extract specific error message from the response
      let errorMessage = 'Login failed. Please try again.';

      if (error.response) {
        // Server responded with an error status
        if (error.response.status === 401) {
          errorMessage =
            'Invalid credentials. Please check your username and password.';
        } else if (error.response.status === 400) {
          errorMessage = 'Please provide both username and password.';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        // Network error
        errorMessage =
          'Network error. Please check your connection and try again.';
      }

      // Use a single state update to prevent multiple re-renders
      setTimeout(() => {
        setIsLoading(false);
      }, 0);

      return { success: false, error, errorMessage };
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/users/logout/`,
        {},
        {
          withCredentials: true,
        }
      );
      setIsAuth(false);
      setUserType(null);
      localStorage.removeItem('usertype');
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    isAuth,
    user,
    userType,
    isLoading,
    login,
    logout,
    setIsAuth,
    setUserType,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
